/*
 * ॐ नमः शिवाय
 *
 * Copyright (c) 2025 Rudra Coresoft Technologies Private Limited
 *
 * This software is licensed under the VIJAYA 1.0 License.
 * See the LICENSE file for the full text.
 */

/**
 * OrganizerResultsPage — /organizer/results
 *
 * The way in to the motion board. Lists the organizer's races, says whether each
 * one's results are published, and links to the screen that declares and
 * publishes them.
 */

import React, { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import APIHelper from '../../api/APIHelper';
import envConfig from '../../config/envConfig';
import { fetchMyRaces, fetchMyEvents, groupRacesByEvent } from '../feed/results/publishApi';
import { labelOf } from '../feed/results/resultsApi';

// Some seeded events carry extremely long titles; they must not stretch the row.
const clamp = {
  overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', display: 'block',
};

const apiHelper = new APIHelper({
  apiUrl: envConfig.backendApiUrl,
  tokenUrl: envConfig.backendTokenUrl,
  clientId: envConfig.clientKey,
  clientSecret: envConfig.clientSecret,
});

const INK = '#241708';
const MUTED = '#6B5844';
const HAIR = 'rgba(36, 23, 8, .12)';

export default function OrganizerResultsPage() {
  const races = useQuery({
    queryKey: ['organizer', 'races'],
    queryFn: ({ signal }) => fetchMyRaces(apiHelper, { signal }),
  });
  // Events are read as well as races: an event with no activities owns no race
  // row, so listing races alone hides it and the organizer cannot tell whether
  // it is missing or simply has nothing to publish yet.
  const events = useQuery({
    queryKey: ['organizer', 'events'],
    queryFn: ({ signal }) => fetchMyEvents(apiHelper, { signal }),
  });

  const [search, setSearch] = useState('');

  const allGroups = groupRacesByEvent(events.data?.rows, races.data?.rows);
  const rows = races.data?.rows || [];

  /**
   * The events and races the search leaves standing.
   *
   * An event matches on its own title OR on any race inside it, and a matching
   * EVENT keeps all of its races: searching "Hyderabad" should show that
   * event's whole card, not just the races whose own name happens to repeat
   * the word. An event kept only because one race matched is narrowed to the
   * races that did, so the answer to "where is my 10K" is one line, not a
   * screen.
   */
  const groups = useMemo(() => {
    const needle = search.trim().toLowerCase();
    if (!needle) return allGroups;
    const has = (value) => String(value || '').toLowerCase().includes(needle);

    return allGroups
      .map((group) => {
        const title = group.event
          ? labelOf({ record_identifier: 'title', data: group.event }, '')
          : '';
        if (has(title)) return group;
        const matched = group.races.filter((race) => has(race.name));
        return matched.length ? { ...group, races: matched } : null;
      })
      .filter(Boolean);
  }, [allGroups, search]);

  const shownRaces = groups.reduce((total, group) => total + group.races.length, 0);

  return (
    <div style={{ maxWidth: 900, margin: '0 auto', padding: '8px 4px 48px' }}>
      <h1 style={{ margin: '0 0 6px', fontSize: 24, fontWeight: 700, color: INK }}>Results</h1>
      <p style={{ margin: '0 0 22px', fontSize: 14, color: MUTED, lineHeight: 1.6 }}>
        Pick a race to mark its winners and publish them. Publishing is what puts a podium on
        the public board; until then nothing of it is visible.
      </p>

      {!races.isPending && !races.isError && rows.length > 0 && (
        <div style={{ display: 'flex', alignItems: 'baseline', gap: 12, flexWrap: 'wrap', marginBottom: 18 }}>
          <label htmlFor="race-search" style={{ fontSize: 13, color: MUTED }}>Find a race</label>
          <input
            id="race-search"
            type="search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Event or race name"
            style={{
              minHeight: 40, flex: '1 1 260px', maxWidth: 360, padding: '0 12px',
              borderRadius: 9, border: `1px solid ${HAIR}`, fontSize: 14, color: INK,
            }}
          />
          <span style={{ fontSize: 13, color: MUTED }}>
            {search.trim() ? `${shownRaces} of ${rows.length} races` : `${rows.length} race${rows.length === 1 ? '' : 's'}`}
          </span>
        </div>
      )}

      {races.isPending && <p style={{ fontSize: 14, color: MUTED }}>Loading your races…</p>}
      {races.isError && (
        <p role="alert" style={{ fontSize: 14, color: '#C2410C' }}>Could not load your races.</p>
      )}

      {!races.isPending && !races.isError && groups.length === 0 && (
        <p style={{ fontSize: 14, color: MUTED }}>
          {search.trim()
            ? `No event or race here matches “${search.trim()}”.`
            : 'No races yet. Create an event with an activity first.'}
        </p>
      )}

      {groups.length > 0 && (
        <div>
          {groups.map((group, index) => {
            const title = group.event
              ? labelOf({ record_identifier: 'title', data: group.event }, `Event ${group.event.id}`)
              : 'Event no longer listed';
            return (
              <section key={group.event ? group.event.id : `orphan-${index}`} style={{ marginBottom: 26 }}>
                <h2 title={title} style={{ ...clamp, margin: '0 0 8px', fontSize: 15, fontWeight: 700, color: INK, maxWidth: '100%' }}>
                  {title}
                </h2>
                {group.races.length === 0 ? (
                  <p style={{ margin: 0, fontSize: 13, color: MUTED, padding: '12px 16px', border: `1px dashed ${HAIR}`, borderRadius: 12 }}>
                    No activities on this event yet, so there is nothing to publish.
                  </p>
                ) : (
                  <ul style={{ listStyle: 'none', margin: 0, padding: 0, border: `1px solid ${HAIR}`, borderRadius: 14, overflow: 'hidden' }}>
                    {group.races.map((race) => (
                      <li key={race.id} style={{ display: 'flex', alignItems: 'center', gap: 14, padding: '14px 18px', borderBottom: `1px solid ${HAIR}`, background: '#fff' }}>
                        <span style={{ flex: '1 1 0', minWidth: 0 }}>
                          <span title={race.name || ''} style={{ ...clamp, fontSize: 15, fontWeight: 600, color: INK }}>
                            {race.name || `Race ${race.id}`}
                          </span>
                        </span>
                        <span style={{
                          flex: 'none', fontSize: 11, fontWeight: 700, letterSpacing: '.08em', padding: '5px 10px', borderRadius: 999,
                          background: race.results_published ? 'rgba(27,127,72,.12)' : 'rgba(36,23,8,.06)',
                          color: race.results_published ? '#1B7F48' : MUTED,
                        }}>
                          {race.results_published ? 'PUBLISHED' : 'NOT PUBLISHED'}
                        </span>
                        <Link to={`/organizer/results/${race.id}`} style={{ flex: 'none', minHeight: 40, display: 'inline-flex', alignItems: 'center', padding: '0 16px', borderRadius: 10, background: INK, color: '#fff', fontSize: 14, fontWeight: 600, textDecoration: 'none' }}>
                          Manage
                        </Link>
                      </li>
                    ))}
                  </ul>
                )}
              </section>
            );
          })}
          <p style={{ fontSize: 12, color: MUTED, margin: 0 }}>
            {groups.length} event{groups.length === 1 ? '' : 's'} · {rows.length} race{rows.length === 1 ? '' : 's'}
          </p>
        </div>
      )}
    </div>
  );
}
